import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { EnlargeDialog } from "@/components/blog/EnlargeDialog";
import { MediaMatrix } from "@/components/blog/MediaMatrix";
import { UploadDialog, type UploadSubmission } from "@/components/blog/UploadDialog";
import {
  deriveMediaMatrix,
  mediaVariantUrl,
  provenanceChip,
  slotLabel,
  variantSlotsFor,
  type MediaTheme,
  type MediaViewport,
} from "@/lib/blog/media-model";
import { LTR_ISOLATE_ATTRS } from "@/lib/blog/locale-options";
import type { BlogMediaState } from "@/lib/blog/types";

interface MediaPanelProps {
  slug: string;
  locale: string;
  state: BlogMediaState;
  /** Screenshot ids this locale's body actually references. */
  requiredScreenshots: string[];
  baseUrl: string;
  uploading: boolean;
  uploadFailure?: string | undefined;
  onUpload: (
    target: { screenshotId: string; theme: MediaTheme; viewport: MediaViewport },
    submission: UploadSubmission,
  ) => void;
}

interface UploadTarget {
  screenshotId: string;
  theme: MediaTheme;
  viewport: MediaViewport;
}

interface EnlargeTarget {
  title: string;
  src: string;
  alt: string;
  hash: string;
}

export function MediaPanel({
  slug,
  locale,
  state,
  requiredScreenshots,
  baseUrl,
  uploading,
  uploadFailure,
  onUpload,
}: MediaPanelProps) {
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const [enlarged, setEnlarged] = useState<EnlargeTarget | null>(null);

  const matrix = deriveMediaMatrix(state, requiredScreenshots);

  return (
    <div className="space-y-6">
      <AdminSectionCard
        title={`Figures in ${locale}`}
        description="Four variants per screenshot: light and dark, desktop and mobile. A missing variant has no fallback — it blocks the publish."
      >
        <div className="grid gap-6">
          {matrix.screenshots.map((screenshotId) => (
            <div key={screenshotId} className="rounded-2xl border p-4">
              <h4 className="font-mono text-sm" {...LTR_ISOLATE_ATTRS}>
                {screenshotId}
              </h4>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {variantSlotsFor(state.objects, { locale, screenshotId }).map((slot) => {
                  const label = slotLabel(slot.theme, slot.viewport);
                  const object = slot.object;
                  if (object === null) {
                    return (
                      <div
                        key={label}
                        className="rounded-xl border border-destructive bg-destructive/10 p-3"
                        data-testid={`variant-missing-${screenshotId}-${slot.theme}-${slot.viewport}`}
                      >
                        <p className="text-xs font-semibold text-destructive">Missing</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            setUploadTarget({
                              screenshotId,
                              theme: slot.theme,
                              viewport: slot.viewport,
                            });
                          }}
                        >
                          Upload
                        </Button>
                      </div>
                    );
                  }

                  const chip = provenanceChip(object);
                  const src = mediaVariantUrl(baseUrl, { slug, object });
                  const alt = `${screenshotId}, ${label}, ${locale}`;
                  return (
                    <div key={label} className="rounded-xl border p-3">
                      <button
                        type="button"
                        className="block w-full"
                        onClick={() => {
                          setEnlarged({
                            title: `${screenshotId} — ${label}`,
                            src,
                            alt,
                            hash: object.hash,
                          });
                        }}
                      >
                        <img src={src} alt={alt} className="h-24 w-full rounded object-cover" />
                      </button>
                      <p className="mt-2 text-xs text-muted-foreground">{label}</p>
                      <p className="mt-1 flex items-center gap-1">
                        <Badge variant="outline">{chip.label}</Badge>
                        <span
                          className="font-mono text-xs text-muted-foreground"
                          {...LTR_ISOLATE_ATTRS}
                        >
                          {chip.hash}
                        </span>
                      </p>
                      {object.uploadNote !== null && (
                        <p className="mt-1 text-xs text-muted-foreground">{object.uploadNote}</p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setUploadTarget({
                            screenshotId,
                            theme: slot.theme,
                            viewport: slot.viewport,
                          });
                        }}
                      >
                        Replace
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </AdminSectionCard>

      <AdminSectionCard
        title="Every screenshot, every language"
        description="Cells below four are defects, not warnings — the publish gate reports the same fact."
      >
        <MediaMatrix matrix={matrix} />
      </AdminSectionCard>

      {uploadTarget !== null && (
        <UploadDialog
          open
          onOpenChange={() => {
            setUploadTarget(null);
          }}
          screenshotId={uploadTarget.screenshotId}
          locale={locale}
          theme={uploadTarget.theme}
          viewport={uploadTarget.viewport}
          uploading={uploading}
          failure={uploadFailure}
          onSubmit={(submission) => {
            onUpload(uploadTarget, submission);
          }}
        />
      )}

      {enlarged !== null && (
        <EnlargeDialog
          open
          onOpenChange={() => {
            setEnlarged(null);
          }}
          title={enlarged.title}
          src={enlarged.src}
          alt={enlarged.alt}
          hash={enlarged.hash}
        />
      )}
    </div>
  );
}
